<template>
  <Transition name="status-tooltip">
    <div
      v-show="visible"
      id="footer-status-tooltip"
      class="kds-footer-status-tooltip"
      role="tooltip"
      :aria-busy="loading ? 'true' : undefined"
    >
      <template v-if="loading">
        <div class="kds-footer-status-tooltip__row" aria-hidden="true">
          <div class="kds-footer-status-tooltip__skeleton-name" />
          <div class="kds-footer-status-tooltip__skeleton-badge" />
        </div>
        <div class="kds-footer-status-tooltip__row" aria-hidden="true">
          <div class="kds-footer-status-tooltip__skeleton-name" />
          <div class="kds-footer-status-tooltip__skeleton-badge" />
        </div>
      </template>
      <template v-else>
        <div
          v-for="service in services"
          :key="service.id"
          class="kds-footer-status-tooltip__row"
        >
          <span class="kds-footer-status-tooltip__name">{{ service.name }}</span>
          <span
            class="kds-footer-status-tooltip__badge"
            :class="`kds-footer-status-tooltip__badge--${service.status}`"
          >{{ badgeLabel(service.status) }}</span>
        </div>
      </template>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { StatusKind } from '@/types/status';
import type { StatusServiceRow } from '@/composables/useStatusTooltip';

interface Props {
  visible: boolean;
  services: StatusServiceRow[];
  loading: boolean;
}

defineProps<Props>();

const { t } = useI18n();

const badgeLabel = (status: StatusKind): string => {
  if (status === 'operational') return t('status.badges.operational');
  if (status === 'degraded') return t('status.badges.degraded');
  if (status === 'outage') return t('status.badges.outage');
  return t('status.badges.unknown');
};
</script>

<style scoped lang="scss">
.kds-footer-status-tooltip {
  position: absolute;
  bottom: calc(100% + var(--space-2));
  right: 0;
  width: 260px;
  max-width: calc(100vw - var(--space-8));
  z-index: 100;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--paper);
  border: var(--border);
  border-radius: var(--r-4);
  box-shadow: var(--shadow-3);

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }

  &__name {
    font-size: var(--fs-body-sm);
    color: var(--ink);
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__badge {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: 2px var(--space-2);
    border-radius: var(--r-pill);
    font-size: var(--fs-meta);
    font-weight: var(--fw-medium);
    letter-spacing: 0.04em;
    text-transform: uppercase;

    &--operational {
      background: color-mix(in srgb, var(--ok) 12%, transparent);
      color: var(--ok);
    }

    &--degraded {
      background: color-mix(in srgb, var(--warn) 12%, transparent);
      color: var(--warn);
    }

    &--outage {
      background: color-mix(in srgb, var(--danger) 12%, transparent);
      color: var(--danger);
    }

    &--unknown {
      background: color-mix(in srgb, var(--stone) 12%, transparent);
      color: var(--stone);
    }
  }

  // Skeleton shimmer
  &__skeleton-name {
    height: 14px;
    width: 120px;
    background: var(--mist);
    border-radius: var(--r-2);
    animation: shimmer 1.4s ease-in-out infinite;
  }

  &__skeleton-badge {
    height: 20px;
    width: 80px;
    background: var(--mist);
    border-radius: var(--r-pill);
    animation: shimmer 1.4s ease-in-out infinite;
  }
}

// Transition: fade + slide-up 120 ms
.status-tooltip-enter-active,
.status-tooltip-leave-active {
  transition:
    opacity var(--d-fast) var(--ease-out),
    transform var(--d-fast) var(--ease-out);
}

.status-tooltip-enter-from,
.status-tooltip-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

@keyframes shimmer {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

@media (hover: none) {
  .kds-footer-status-tooltip {
    display: none;
  }
}
</style>
